import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { CartItemGift } from '@sola/schema/dotcom/cart/gift.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type {
  GiftLimits,
  GiftTypeLimits,
} from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import { GiftFundCodesEnum } from '@sola/schema/dotcom/shared/gifts.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';

import { isItemGiftCenter } from './cart-item-type-guards.ts';

export const GiftTypeMapByFundCode: Record<string, GiftTypeEnum> = {
  [GiftFundCodesEnum.Birthday]: GiftTypeEnum.Birthday,
  [GiftFundCodesEnum.Child]: GiftTypeEnum.Child,
  [GiftFundCodesEnum.Family]: GiftTypeEnum.Family,
  [GiftFundCodesEnum.Center]: GiftTypeEnum.Center,
  [GiftFundCodesEnum.Final]: GiftTypeEnum.Final,
} as const;

export const GiftIconMap: Record<GiftTypeEnum, IconNames> = {
  [GiftTypeEnum.Birthday]: 'birthday-cake',
  [GiftTypeEnum.Center]: 'church',
  [GiftTypeEnum.Child]: 'gift-01',
  [GiftTypeEnum.Family]: 'family-gift',
  [GiftTypeEnum.Final]: 'beneficiary',
} as const;

/** Fallback min/max per gift type when the gift-limits API is unavailable. */
export const DefaultGiftLimits: Record<
  GiftTypeEnum,
  { min: number; max: number }
> = {
  [GiftTypeEnum.Birthday]: { min: 10, max: 150 },
  [GiftTypeEnum.Child]: { min: 10, max: 150 },
  [GiftTypeEnum.Family]: { min: 10, max: 1100 },
  [GiftTypeEnum.Center]: { min: 50, max: 15_000 },
  [GiftTypeEnum.Final]: { min: 10, max: 3000 },
} as const;

export const GiftIconMapByFundCode: Record<GiftFundCodesEnum, IconNames> = {
  [GiftFundCodesEnum.Birthday]: GiftIconMap[GiftTypeEnum.Birthday],
  [GiftFundCodesEnum.Center]: GiftIconMap[GiftTypeEnum.Center],
  [GiftFundCodesEnum.Child]: GiftIconMap[GiftTypeEnum.Child],
  [GiftFundCodesEnum.Family]: GiftIconMap[GiftTypeEnum.Family],
  [GiftFundCodesEnum.Final]: GiftIconMap[GiftTypeEnum.Final],
} as const;

export const getGiftLimitMatch = (
  cartItem: CartItem & CartItemGift,
  giftLimits: GiftLimits,
): GiftTypeLimits | undefined => {
  return isItemGiftCenter(cartItem)
    ? giftLimits?.projects?.[cartItem.gift.centerId]
    : giftLimits?.children?.[cartItem.child?.globalId || ''];
};
